import { tr } from '@/lib/i18n-core';
import { formatMoneyInput, minorFromInput, parseMoneyInput } from '@/components/MoneyInput';
import { TaxCategorySelect } from '@/components/TaxCategorySelect';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Building2, Coins, FlaskConical, ScanLine, Tag } from 'lucide-react';
import {
  BENEFIT_CATEGORY_PLAIN,
  ENCOUNTER_MODULES,
  RADIOLOGY_MODALITY,
  catalogueCodeForService,
  countryProfile,
  servicePriceRequired,
  type ServiceCatalogueLinks,
  type ServiceImagingDetails,
  type ServiceLabDetails,
} from '@his/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useBranchLock } from '@/lib/branch';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Checkbox, Field, FormSkeleton, Input, Select, Switch } from '@/components/ui/primitives';
import { humanize } from '@/lib/utils';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { DepartmentsPicker, useChargeCategories, useNphies, type ChargeItem, type DepartmentOption } from './ChargeMasterPage';
import { ServiceDoctorsSection, doctorRowsBody, rowsFromViews, type ServiceDoctorRow, type ServiceDoctorView } from './ServiceDoctorsSection';

const OTHER_BENEFIT = 'other';
const LAB_GROUPS = ['hematology', 'biochemistry', 'microbiology', 'serology', 'histopathology', 'molecular', 'other'] as const;
const RESULT_TYPES = ['numeric', 'text', 'select', 'culture'] as const;
const DEFAULT_TAT_MINUTES = 240;
const MIN_TAT_MINUTES = 5;

function benefitChoiceOf(value: string): string {
  if (!value) return '';
  return (BENEFIT_CATEGORY_PLAIN as readonly string[]).includes(value) ? value : OTHER_BENEFIT;
}

function hoursFromMinutes(minutes: number): string {
  return String(Number((minutes / 60).toFixed(2)));
}

function minutesFromHours(hours: string): number {
  const value = Number(hours.replace(',', '.'));
  return Number.isFinite(value) ? Math.max(MIN_TAT_MINUTES, Math.round(value * 60)) : DEFAULT_TAT_MINUTES;
}

interface LabForm {
  departmentGroup: string;
  specimenType: string;
  containerType: string;
  tatHours: string;
  resultType: string;
  unit: string;
}

interface ImagingForm {
  modality: string;
  bodyPart: string;
  requiresContrast: boolean;
  requiresAppointment: boolean;
}

function labForm(catalogue: ServiceCatalogueLinks | null): LabForm {
  const test = catalogue?.labTest ?? null;
  return {
    departmentGroup: test?.departmentGroup ?? 'other',
    specimenType: test?.specimenType ?? '',
    containerType: test?.containerType ?? '',
    tatHours: hoursFromMinutes(test?.tatMinutes ?? DEFAULT_TAT_MINUTES),
    resultType: test?.resultType ?? 'numeric',
    unit: test?.unit ?? '',
  };
}

function imagingForm(catalogue: ServiceCatalogueLinks | null): ImagingForm {
  const study = catalogue?.imaging ?? null;
  return {
    modality: study?.modality ?? 'xray',
    bodyPart: study?.bodyPart ?? '',
    requiresContrast: study?.requiresContrast ?? false,
    requiresAppointment: study?.requiresAppointment ?? false,
  };
}

function labBody(lab: LabForm): ServiceLabDetails {
  return {
    departmentGroup: lab.departmentGroup as ServiceLabDetails['departmentGroup'],
    specimenType: lab.specimenType.trim(),
    containerType: lab.containerType.trim() || null,
    tatMinutes: minutesFromHours(lab.tatHours),
    resultType: lab.resultType as ServiceLabDetails['resultType'],
    unit: lab.resultType === 'numeric' ? lab.unit.trim() || null : null,
  };
}

function imagingBody(imaging: ImagingForm): ServiceImagingDetails {
  return {
    modality: imaging.modality as ServiceImagingDetails['modality'],
    bodyPart: imaging.bodyPart.trim() || null,
    requiresContrast: imaging.requiresContrast,
    requiresAppointment: imaging.requiresAppointment,
  };
}

function toForm(item: ChargeItem | null, doctors: ServiceDoctorView[], catalogue: ServiceCatalogueLinks | null) {
  const benefitCategory = item?.benefitCategory ?? '';
  return {
    lab: labForm(catalogue),
    imaging: imagingForm(catalogue),
    code: item?.code ?? '',
    name: item?.name ?? '',
    nameLocal: item?.nameLocal ?? '',
    category: item?.category ?? 'consultation',
    departmentIds: item ? (item.departments.length ? item.departments.map((department) => department.id) : item.department ? [item.department.id] : []) : [],
    duration: String(item?.durationMinutes ?? 0),
    nphiesClaimable: item?.nphiesClaimable ?? false,
    nphiesPrice: formatMoneyInput(item?.nphiesPriceMinor ?? null),
    basePrice: item ? formatMoneyInput(item.basePriceMinor) : '0',
    taxCategory: item?.taxCategory ?? '',
    benefitCategory,
    benefitOther: benefitChoiceOf(benefitCategory) === OTHER_BENEFIT,
    doctors: rowsFromViews(doctors),
  };
}

export function ChargeItemPage() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const usesNphies = useNphies();
  const returnTo = safeReturnTo(params.get('returnTo'), '/billing/charges');

  const items = useQuery({
    queryKey: ['charge-items', 'one', id, usesNphies],
    queryFn: () => get<{ data: ChargeItem[] }>(`/charge-items${qs({ withNphies: usesNphies ? 1 : undefined, limit: 500 })}`),
    enabled: Boolean(id),
  });
  const doctors = useQuery({
    queryKey: ['charge-item-doctors', id],
    queryFn: () => get<ServiceDoctorView[]>(`/charge-items/${id}/doctors`),
    enabled: Boolean(id),
  });
  const catalogue = useQuery({
    queryKey: ['charge-item-catalogue', id],
    queryFn: () => get<ServiceCatalogueLinks>(`/charge-items/${id}/catalogue`),
    enabled: Boolean(id),
  });

  if (!id) return <ChargeItemForm key="new" item={null} doctors={[]} catalogue={null} returnTo={returnTo} />;
  if (items.isLoading || doctors.isLoading || catalogue.isLoading) return <FormSkeleton />;
  const item = items.data?.data.find((row) => row.id === id);
  if (!item) return <Alert variant="destructive">{tr('billing', 'that_record_could_not_be_opened')}</Alert>;
  return <ChargeItemForm key={item.id} item={item} doctors={doctors.data ?? []} catalogue={catalogue.data ?? null} returnTo={returnTo} />;
}

function ChargeItemForm({
  item,
  doctors,
  catalogue,
  returnTo,
}: {
  item: ChargeItem | null;
  doctors: ServiceDoctorView[];
  catalogue: ServiceCatalogueLinks | null;
  returnTo: string;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState(() => toForm(item, doctors, catalogue));
  const categories = useChargeCategories();
  const auth = useAuth();
  const lock = useBranchLock();
  const branchId = item?.branchId ?? lock.branchId;
  const branchName = (auth.user?.branches ?? []).find((branch) => branch.id === branchId)?.name;
  const localLanguages = new Set((auth.user?.branches ?? []).map((branch) => countryProfile(branch.country ?? null).locales.documentSecondary).filter(Boolean));
  const localLabel = localLanguages.size === 1 && localLanguages.has('ar') ? 'Name (Arabic)' : 'Name (local language)';
  const showsShareLink =
    auth.can('doctor_share.read', 'doctor_share.manage') && auth.hasModule('billing') && ENCOUNTER_MODULES.some((module) => auth.hasModule(module));
  const usesNphies = useNphies();
  const benefitMissing = usesNphies && form.nphiesClaimable && !form.benefitCategory.trim();
  const nphiesPriceMinor = form.nphiesPrice.trim() ? parseMoneyInput(form.nphiesPrice) : null;
  const nphiesPriceInvalid = usesNphies && form.nphiesClaimable && form.nphiesPrice.trim() !== '' && nphiesPriceMinor === null;
  const nphiesOverrides = item?.nphiesOverrides ?? 0;
  const benefitChoice = form.benefitOther ? OTHER_BENEFIT : benefitChoiceOf(form.benefitCategory);
  const inHouseModules = (['lab', 'radiology'] as const).filter((module) => auth.hasModule(module));
  const linkedPanel = catalogue?.labPanel ?? null;
  const showsLab = form.category === 'lab' && auth.hasModule('lab') && !linkedPanel;
  const showsImaging = form.category === 'radiology' && auth.hasModule('radiology');
  const priceRequired = servicePriceRequired(form.category, inHouseModules);
  const priceMinor = minorFromInput(form.basePrice);
  const priceMissing = priceRequired && !(priceMinor > 0);
  const labIncomplete = showsLab && form.lab.specimenType.trim().length < 2;
  const priceHint =
    form.category === 'procedure'
      ? tr('billing', 'price_required_procedure')
      : form.category === 'lab'
        ? tr('billing', priceRequired ? 'price_required_lab' : 'price_optional_lab')
        : form.category === 'radiology'
          ? tr('billing', priceRequired ? 'price_required_radiology' : 'price_optional_radiology')
          : undefined;

  const departments = useQuery({
    queryKey: ['departments'],
    queryFn: () => get<DepartmentOption[]>('/org/departments'),
    select: (rows) => (branchId ? rows.filter((row) => row.branchId === branchId) : rows),
  });

  const initialDoctors = JSON.stringify(doctorRowsBody(rowsFromViews(doctors)));

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        code: form.code,
        name: form.name,
        ...(localLanguages.size > 0 ? { nameLocal: form.nameLocal.trim() || null } : {}),
        category: form.category,
        departmentId: form.departmentIds[0] ?? null,
        departmentIds: form.departmentIds,
        ...(item ? {} : { branchId: branchId || null }),
        durationMinutes: Math.max(0, Math.round(Number(form.duration) || 0)),
        nphiesClaimable: form.nphiesClaimable,
        taxCategory: form.taxCategory || null,
        basePriceMinor: minorFromInput(form.basePrice),
        ...(usesNphies ? { benefitCategory: form.benefitCategory.trim() || null } : {}),
        ...(showsLab ? { labTest: labBody(form.lab) } : {}),
        ...(showsImaging ? { imaging: imagingBody(form.imaging) } : {}),
        isActive: true,
      };
      const saved = item ? await put<ChargeItem>(`/charge-items/${item.id}`, body) : await post<ChargeItem>('/charge-items', body);
      if (usesNphies && form.nphiesClaimable && form.nphiesPrice.trim() !== formatMoneyInput(item?.nphiesPriceMinor ?? null)) {
        await put(`/charge-items/${saved.id}/nphies-price`, { priceMinor: nphiesPriceMinor });
      }
      const doctorRows = doctorRowsBody(form.doctors);
      if (JSON.stringify(doctorRows) !== initialDoctors) await put(`/charge-items/${saved.id}/doctors`, { doctors: doctorRows });
      return saved;
    },
    onSuccess: () => {
      toast.success(item ? tr('billing', 'service_updated') : tr('billing', 'service_added'));
      void queryClient.invalidateQueries({ queryKey: ['charge-items'] });
      void queryClient.invalidateQueries({ queryKey: ['charge-item-doctors'] });
      void queryClient.invalidateQueries({ queryKey: ['charge-item-catalogue'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const dirty = !save.isSuccess && JSON.stringify(form) !== JSON.stringify(toForm(item, doctors, catalogue));
  const incomplete =
    !form.code || !form.name || benefitMissing || nphiesPriceInvalid || priceMissing || labIncomplete || form.doctors.some((row) => !row.doctorId);
  const setLab = (patch: Partial<LabForm>) => setForm({ ...form, lab: { ...form.lab, ...patch } });
  const setImaging = (patch: Partial<ImagingForm>) => setForm({ ...form, imaging: { ...form.imaging, ...patch } });

  return (
    <FormScreen
      title={item ? tr('billing', 'edit_service') : tr('billing', 'add_service')}
      eyebrow={tr('billing', 'charge_master')}
      icon={Tag}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (incomplete) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('billing', 'service')} icon={Tag}>
        <Field label={tr('billing', 'code')} required>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
        </Field>
        <Field label={tr('billing', 'category')} required>
          <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {categories.map((c) => (
              <option key={c} value={c}>
                {humanize(c, 'charge_category')}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('billing', 'name')} required className="sm:col-span-2">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        {localLanguages.size > 0 && (
          <Field label={localLabel} className="sm:col-span-2" hint={tr('billing', 'printed_beside_the_english_name_on_bilingual')}>
            <Input dir="auto" value={form.nameLocal} onChange={(e) => setForm({ ...form, nameLocal: e.target.value })} />
          </Field>
        )}
      </FormSection>

      <FormSection title={tr('billing', 'departments')} icon={Building2}>
        <Field label={tr('billing', 'departments')} hint={tr('billing', 'the_first_department_chosen_is_where_revenue')}>
          <DepartmentsPicker options={departments.data ?? []} value={form.departmentIds} onChange={(departmentIds) => setForm({ ...form, departmentIds })} />
          {branchName && <p className="mt-1 text-xs text-muted-foreground">{tr('billing', 'offered_at_x', { branch: branchName })}</p>}
        </Field>
        <Field label={tr('billing', 'duration_minutes')} hint={tr('billing', 'zero_means_the_length_is_asked_for_at_booking')}>
          <Input type="number" min={0} max={1440} step={5} value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="tabular" />
        </Field>
      </FormSection>

      <FormSection title={tr('billing', 'pricing')} icon={Coins}>
        <Field
          label={tr('billing', 'list_price')}
          required
          hint={priceHint}
          error={priceMissing ? tr('billing', 'enter_a_price_above_zero') : undefined}
        >
          <Input
            type="text"
            inputMode="decimal"
            dir="ltr"
            value={form.basePrice}
            aria-invalid={priceMissing || undefined}
            onChange={(e) => setForm({ ...form, basePrice: e.target.value })}
            className="tabular"
          />
        </Field>
        <div>
          <TaxCategorySelect value={form.taxCategory} onChange={(taxCategory) => setForm({ ...form, taxCategory })} />
          <p className="mt-1 text-xs text-muted-foreground">
            {tr('billing', 'the_tax_rate_comes_from_the_branch_tax_rates')}{' '}
            <Link className="text-primary underline-offset-4 hover:underline" to="/billing/settings?tab=tax">
              {tr('billing', 'tax_rates')}
            </Link>
          </p>
        </div>
        {usesNphies && (
          <Field label={tr('billing', 'nphies')}>
            <Switch
              checked={form.nphiesClaimable}
              onCheckedChange={(nphiesClaimable) => setForm({ ...form, nphiesClaimable })}
              label={tr('billing', 'claimable_through_nphies')}
            />
          </Field>
        )}
        {usesNphies && form.nphiesClaimable && (
          <Field
            label={tr('billing', 'nphies_price')}
            hint={
              nphiesOverrides > 0
                ? `${tr('billing', 'the_price_nphies_payers_get_leave_it_empty')}. ${tr('billing', 'payer_price_lists_that_override_this_amount', { count: nphiesOverrides })}`
                : tr('billing', 'the_price_nphies_payers_get_leave_it_empty')
            }
            error={nphiesPriceInvalid ? tr('billing', 'nphies_price_invalid', { example: formatMoneyInput(150000) }) : undefined}
          >
            <Input
              type="text"
              inputMode="decimal"
              dir="ltr"
              value={form.nphiesPrice}
              placeholder={form.basePrice}
              aria-invalid={nphiesPriceInvalid || undefined}
              onChange={(e) => setForm({ ...form, nphiesPrice: e.target.value })}
              className="tabular"
            />
          </Field>
        )}
        {usesNphies && form.nphiesClaimable && (
          <>
            <Field label={tr('billing', 'benefit_category')} required hint={tr('billing', 'how_an_insurance_policy_groups_this_service_for')}>
              <Select
                value={benefitChoice}
                onChange={(e) => {
                  const choice = e.target.value;
                  if (choice === OTHER_BENEFIT) setForm({ ...form, benefitCategory: '', benefitOther: true });
                  else setForm({ ...form, benefitCategory: choice, benefitOther: false });
                }}
              >
                <option value="">{tr('billing', 'required_when_nphies_is_in_use')}</option>
                {BENEFIT_CATEGORY_PLAIN.map((value) => (
                  <option key={value} value={value}>
                    {humanize(value, 'benefit_category')}
                  </option>
                ))}
                <option value={OTHER_BENEFIT}>{tr('billing', 'benefit_category_other')}</option>
              </Select>
            </Field>
            {benefitChoice === OTHER_BENEFIT && (
              <Field label={tr('billing', 'benefit_category_name')} required>
                <Input value={form.benefitCategory} onChange={(e) => setForm({ ...form, benefitCategory: e.target.value, benefitOther: true })} />
              </Field>
            )}
          </>
        )}
        {showsShareLink && (
          <p className="text-sm text-muted-foreground sm:col-span-2">
            {tr('billing', 'the_doctors_share_of_this_service_is_set_in')}{' '}
            <Link className="text-primary underline-offset-4 hover:underline" to="/accounts/doctor-share">
              {tr('billing', 'doctor_share')}
            </Link>
          </p>
        )}
      </FormSection>

      {form.category === 'lab' && linkedPanel && (
        <Alert className="flex items-center gap-2">
          <FlaskConical className="h-4 w-4 shrink-0 text-primary" aria-hidden />
          <span>{tr('billing', 'linked_lab_panel_x', { code: linkedPanel.code })}</span>
        </Alert>
      )}

      {showsLab && (
        <FormSection
          title={tr('billing', 'lab_test_details')}
          description={
            catalogue?.labTest
              ? tr('billing', 'linked_lab_test_x', { code: catalogue.labTest.code })
              : form.code.trim()
                ? tr('billing', 'new_lab_test_x', { code: catalogueCodeForService(form.code, 'LAB') })
                : tr('billing', 'lab_test_details_hint')
          }
          icon={FlaskConical}
          columns={3}
        >
          <Field label={tr('billing', 'lab_section')}>
            <Select value={form.lab.departmentGroup} onChange={(e) => setLab({ departmentGroup: e.target.value })}>
              {LAB_GROUPS.map((group) => (
                <option key={group} value={group}>
                  {humanize(group)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={tr('lab', 'specimen')} required>
            <Input value={form.lab.specimenType} onChange={(e) => setLab({ specimenType: e.target.value })} placeholder={tr('lab', 'whole_blood_edta')} />
          </Field>
          <Field label={tr('lab', 'container')}>
            <Input value={form.lab.containerType} onChange={(e) => setLab({ containerType: e.target.value })} placeholder={tr('lab', 'lavender_top')} />
          </Field>
          <Field label={tr('billing', 'turnaround_hours')}>
            <Input
              type="number"
              min={0.1}
              step={0.5}
              dir="ltr"
              value={form.lab.tatHours}
              onChange={(e) => setLab({ tatHours: e.target.value })}
              className="tabular"
            />
          </Field>
          <Field label={tr('lab', 'result_type')}>
            <ChoiceButtons
              layout="chips"
              value={form.lab.resultType}
              onChange={(resultType) => setLab({ resultType })}
              options={RESULT_TYPES.map((type) => ({ value: type, label: humanize(type, 'lab_result_type') }))}
            />
          </Field>
          <Field label={tr('lab', 'unit')}>
            <Input
              value={form.lab.unit}
              disabled={form.lab.resultType !== 'numeric'}
              onChange={(e) => setLab({ unit: e.target.value })}
              placeholder={tr('lab', 'g_dl')}
            />
          </Field>
        </FormSection>
      )}

      {showsImaging && (
        <FormSection
          title={tr('billing', 'imaging_details')}
          description={
            catalogue?.imaging
              ? tr('billing', 'linked_imaging_study_x', { code: catalogue.imaging.code })
              : form.code.trim()
                ? tr('billing', 'new_imaging_study_x', { code: catalogueCodeForService(form.code, 'RAD') })
                : tr('billing', 'imaging_details_hint')
          }
          icon={ScanLine}
        >
          <Field label={tr('radiology', 'modality')}>
            <Select value={form.imaging.modality} onChange={(e) => setImaging({ modality: e.target.value })}>
              {RADIOLOGY_MODALITY.map((modality) => (
                <option key={modality} value={modality}>
                  {humanize(modality, 'radiology_modality')}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={tr('billing', 'body_part')}>
            <Input value={form.imaging.bodyPart} onChange={(e) => setImaging({ bodyPart: e.target.value })} />
          </Field>
          <div className="flex flex-col gap-2 sm:col-span-2">
            <Checkbox
              checked={form.imaging.requiresContrast}
              onCheckedChange={(checked) => setImaging({ requiresContrast: checked })}
              label={tr('billing', 'needs_contrast')}
            />
            <Checkbox
              checked={form.imaging.requiresAppointment}
              onCheckedChange={(checked) => setImaging({ requiresAppointment: checked })}
              label={tr('billing', 'needs_an_appointment')}
            />
          </div>
        </FormSection>
      )}

      <ServiceDoctorsSection rows={form.doctors} onChange={(rows) => setForm({ ...form, doctors: rows })} basePriceMinor={minorFromInput(form.basePrice)} />

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('billing', 'save')} loading={save.isPending} incomplete={incomplete} cancelTo={returnTo} />
    </FormScreen>
  );
}
